// <!--/assets/regimens.js-->
// STS_REGIMENS_V2 — the "how to apply your routine" page, rebuilt on the
// current quiz engine (2026-08-21).
//
// The original page read `customerRoutine` from localStorage — a key only the
// retired Octane-era quiz ever wrote — so everyone who took the current quiz
// was silently bounced back to the quiz landing, and pre-go-live customers saw
// a routine the engine no longer prescribes. This version resolves the routine
// exactly the way the results page does, in the same priority order:
//
//   1. sessionStorage `slqResultsHandoff` — this tab just finished the quiz
//   2. localStorage  `slqSavedResults`    — a taker who saved their results
//      (either carries a widget token → live GET {EC2}/widget/regimen)
//   3. localStorage  `slqSavedRegimen`    — the 30-day cached copy the results
//      page writes for EVERY taker on a successful live render
//   4. nothing → the quiz landing, same as the page always did
//
// What renders is the old page's experience — AM / PM step cards with the
// per-slot application-instruction carousels (product_instruction_steps
// metaobjects), the product modal with add-to-cart, and the Complete/Starter
// toggle (now driven by the payload's own `tier` field). The markup keeps the
// original class names so quiz-results-main.css and regimens.css style it
// unchanged.

(async () => {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* setup                                                               */
  /* ------------------------------------------------------------------ */

  var savedValuesEl = document.querySelector('[data-saved-values]');
  var savedValues = {};
  try { savedValues = JSON.parse(savedValuesEl ? savedValuesEl.textContent : '{}') || {}; } catch (e) { /* keep {} */ }

  var storefrontAccessToken = savedValues.storefrontAccessToken || '';
  var defaultImage = savedValues.defaultImage || '';
  var defaultProductImage = savedValues.defaultProductImage || '';
  var defaultAltText = 'Image';

  var regimensPage = document.querySelector('[data-regimens]');
  if (!regimensPage) return;
  var noProductsError = regimensPage.getAttribute('data-no-products') || 'No products in this routine yet.';
  var loader = document.querySelector('[data-loader]');
  var body = document.querySelector('body');
  var drawer = document.querySelector('[data-drawer="drawer-cart"]');

  // The section can carry an override, but the storefront talks to prod.
  var EC2_BASE = (regimensPage.getAttribute('data-ec2-base') || 'https://api.skintype.pro').replace(/\/+$/, '');
  var QUIZ_URL = '/pages/skin-type-quiz';
  var RESULTS_URL = '/pages/skin-type-results';
  var GRAPHQL_URL = '/api/2024-01/graphql.json';

  var toggleLoader = function (on) {
    if (!loader) return;
    if (on) loader.classList.add('--active');
    else loader.remove();
  };

  /* ------------------------------------------------------------------ */
  /* routine resolution — mirrors sections/sts-liquid-results.liquid      */
  /* ------------------------------------------------------------------ */

  function readJSON(storage, key) {
    try { return JSON.parse(storage.getItem(key) || 'null'); } catch (e) { return null; }
  }

  var handoff = readJSON(window.sessionStorage, 'slqResultsHandoff');
  var saved = readJSON(window.localStorage, 'slqSavedResults');
  var widgetToken = (handoff && handoff.widgetToken) || (saved && saved.widgetToken) || '';
  var skinType = String(
    (handoff && handoff.skinType) || (saved && saved.skinType) ||
    window.localStorage.getItem('customerSkinType') || ''
  ).toUpperCase();

  /* Same rules as the results page: 30 days max, never an empty list, and a
   * cache written for a DIFFERENT skin type (shared device, or pre-re-quiz) is
   * someone else's routine — never render it. */
  var CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  function readCachedRegimen() {
    var c = readJSON(window.localStorage, 'slqSavedRegimen');
    if (!c || !Array.isArray(c.steps) || !c.steps.length) return null;
    if (!c.cachedAt || (Date.now() - c.cachedAt) > CACHE_MAX_AGE_MS) return null;
    if (skinType && c.skinType && String(c.skinType).toUpperCase() !== skinType) return null;
    return c;
  }

  async function fetchLiveRegimen() {
    if (!widgetToken) return null;
    try {
      var res = await fetch(EC2_BASE + '/widget/regimen', {
        headers: { Authorization: 'Bearer ' + widgetToken }
      });
      if (!res.ok) return null;
      var data = await res.json();
      if (!data || !Array.isArray(data.steps) || !data.steps.length) return null;
      return data.steps;
    } catch (e) { return null; }
  }

  var rawSteps = await fetchLiveRegimen();
  if (!rawSteps) {
    var cached = readCachedRegimen();
    if (cached) {
      rawSteps = cached.steps;
      // The cache remembers whose routine it is even when the handoff and the
      // legacy customerSkinType key are long gone — keep the "Built for your
      // Baumann Skin Type" line alive on the cache-only path.
      if (!skinType && cached.skinType) skinType = String(cached.skinType).toUpperCase();
    }
  }
  if (!rawSteps || !rawSteps.length) {
    // Nothing to show for this visitor — same exit the page always had.
    window.location.href = QUIZ_URL;
    return;
  }

  /* ------------------------------------------------------------------ */
  /* results-page design language                                        */
  /* ------------------------------------------------------------------ */

  /* The exact hand-picked per-type palette the results engine carries
   * (TYPE_PALETTE in sts-liquid-quiz-results.js), with its NEUTRAL fallback.
   * regimens.css styles everything off these custom properties. */
  var TYPE_PALETTE = {
    DSPT: { hex: '#A65E94', deep: '#85377F', tint: '#D3B0CF' },
    DSNT: { hex: '#D88AAE', deep: '#CC5D92', tint: '#EDCCDF' },
    DSPW: { hex: '#CB5D77', deep: '#C3365A', tint: '#E5B2BB' },
    DSNW: { hex: '#DEA796', deep: '#CD7D6B', tint: '#F2DAD1' },
    OSPT: { hex: '#D68762', deep: '#C95C3A', tint: '#EEC9AD' },
    OSNT: { hex: '#EEC8A7', deep: '#E2A77D', tint: '#F9EADB' },
    OSPW: { hex: '#E2A857', deep: '#D48132', tint: '#F5DAA9' },
    OSNW: { hex: '#F5DA95', deep: '#EDC369', tint: '#FCF1D0' },
    ORPT: { hex: '#ABC33F', deep: '#82B142', tint: '#DEE49B' },
    ORNT: { hex: '#C3D690', deep: '#A1C266', tint: '#EAEFCC' },
    ORPW: { hex: '#86B15A', deep: '#629F48', tint: '#CADBA9' },
    ORNW: { hex: '#A0C8AB', deep: '#75B187', tint: '#D9E8DB' },
    DRPT: { hex: '#6194B0', deep: '#35768D', tint: '#B3CBDD' },
    DRNT: { hex: '#9CB1D8', deep: '#718FC2', tint: '#D0DFF0' },
    DRPW: { hex: '#5970A3', deep: '#3B4D85', tint: '#ABB5D6' },
    DRNW: { hex: '#A498C1', deep: '#7D6DA3', tint: '#D7D2E6' }
  };
  var pal = TYPE_PALETTE[skinType] || { hex: '#7B85B4', deep: '#5F6BA8', tint: '#E2E4F0' };
  regimensPage.style.setProperty('--slq-type', pal.hex);
  regimensPage.style.setProperty('--slq-type-deep', pal.deep);
  regimensPage.style.setProperty('--slq-type-tint', pal.tint);

  /* Category labels per slot family — same table as the results page. */
  var CATEGORY_LABELS = [
    ['HA', 'Hypoallergenic'], ['C', 'Cleanser'], ['E', 'Eye Cream'],
    ['M', 'Moisturizer'], ['S', 'Sunscreen'], ['T', 'Treatment']
  ];
  function categoryForSlot(slot) {
    if (!slot) return null;
    var normalized = String(slot).trim().toUpperCase();
    for (var i = 0; i < CATEGORY_LABELS.length; i++) {
      if (normalized.indexOf(CATEGORY_LABELS[i][0]) === 0) return CATEGORY_LABELS[i][1];
    }
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* normalisation                                                       */
  /* ------------------------------------------------------------------ */

  /* Never tell someone to apply a product they cannot buy: an out-of-stock
   * step is replaced by its first in-stock, slot-matched swap candidate —
   * the same substitution rule the results page applies. If nothing in the
   * slot is in stock the step renders as-is rather than inventing one. */
  function substituteOutOfStock(step) {
    if (!step || step.inStock !== false) return step;
    var c = (step.swapCandidates || []).find(function (x) {
      return x && x.dtcVariantId && x.title && x.inStock !== false;
    });
    if (!c) return step;
    return Object.assign({}, step, {
      title: c.title, brand: c.brand, image: c.image, priceMsrp: c.priceMsrp,
      dtcVariantId: c.dtcVariantId, inStock: c.inStock, handle: c.handle,
      substituted: true
    });
  }

  var allSteps = rawSteps
    .filter(function (s) { return s && (s.title || s.slot); })
    .map(function (s, i) {
      var step = substituteOutOfStock(Object.assign({}, s));
      step.time = (step.time === 'AM' || step.time === 'PM' || step.time === 'BOTH') ? step.time : 'BOTH';
      step.key = 'k' + i;
      return step;
    });

  var starterSteps = allSteps.filter(function (s) { return s.tier === 'starter'; });
  // Only offer the toggle when the two sizes actually differ — mirrors the
  // results page, which hides "5+ Step" when they are identical.
  var sizesDiffer = starterSteps.length > 0 && starterSteps.length < allSteps.length;

  var showTypes = ['less', 'full']; // less = Starter, full = Complete
  regimensPage.dataset.productsShow = 'full';

  var byKey = {};
  allSteps.forEach(function (s) { byKey[s.key] = s; });

  var filterByTime = function (steps, time) {
    return steps.filter(function (s) { return s.time === time || s.time === 'BOTH'; });
  };

  /* ------------------------------------------------------------------ */
  /* Shopify storefront lookups                                          */
  /* ------------------------------------------------------------------ */

  var gqlOptions = function (query) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/graphql',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken
      },
      body: query
    };
  };

  // One batched query for every product in the routine — powers the modal
  // (live price, availability, description, ingredient + skin-type metafields).
  var queryOfProducts = function (idsJson) {
    return '{ nodes(ids: ' + idsJson + ') { ... on ProductVariant { availableForSale id title price { amount currencyCode } product { id title handle description featuredImage { url altText } ingredients: metafield(namespace: "product_info" key: "ingredients") { value } skinTypes: metafield(namespace: "product_info" key: "baumann_skin_type_info") { value } } } } }';
  };

  var productDetailByVariant = {}; // numeric variant id -> GQL node
  async function loadProductDetails() {
    var gids = allSteps
      .filter(function (s) { return s.dtcVariantId; })
      .map(function (s) { return 'gid://shopify/ProductVariant/' + s.dtcVariantId; });
    if (!gids.length) return;
    try {
      var res = await fetch(GRAPHQL_URL, gqlOptions(queryOfProducts(JSON.stringify(gids))));
      var data = await res.json();
      (data && data.data && data.data.nodes || []).forEach(function (node) {
        if (!node || !node.id) return;
        productDetailByVariant[node.id.split('/').pop()] = node;
      });
    } catch (e) { /* modal falls back to the step's own fields */ }
  }

  // Application instructions live in `product_instruction_steps` metaobjects,
  // one per SLOT (handle = slot code; lookup is case-insensitive). Tinted
  // sunscreen slots (S25T…) have no metaobject of their own — the base slot's
  // instructions apply, so the trailing T is retried without.
  var instructionCache = {};
  var queryInstructionSteps = function (slotId) {
    return '{ metaobject(handle: {handle: "' + slotId + '", type: "product_instruction_steps"}) { id handle fields { key type value } } }';
  };
  async function fetchInstructionSet(slot) {
    if (!slot) return null;
    var key = String(slot).toUpperCase();
    if (key in instructionCache) return instructionCache[key];
    var fields = null;
    try {
      var res = await fetch(GRAPHQL_URL, gqlOptions(queryInstructionSteps(key)));
      var data = await res.json();
      fields = (data && data.data && data.data.metaobject && data.data.metaobject.fields) || null;
      if (!fields && /^S\d+T$/.test(key)) {
        var res2 = await fetch(GRAPHQL_URL, gqlOptions(queryInstructionSteps(key.slice(0, -1))));
        var data2 = await res2.json();
        fields = (data2 && data2.data && data2.data.metaobject && data2.data.metaobject.fields) || null;
      }
    } catch (e) { /* no instructions is fine */ }
    instructionCache[key] = fields;
    return fields;
  }

  var imageUrlCache = {};
  var queryImage = function (id) {
    return '{ node(id: "' + id + '") { ... on MediaImage { image { url altText } } } }';
  };
  async function fetchImageURL(id) {
    if (!id) return null;
    if (id in imageUrlCache) return imageUrlCache[id];
    var image = null;
    try {
      var res = await fetch(GRAPHQL_URL, gqlOptions(queryImage(id)));
      var data = await res.json();
      image = (data && data.data && data.data.node && data.data.node.image) || null;
    } catch (e) { /* placeholder renders instead */ }
    imageUrlCache[id] = image;
    return image;
  }

  /* ------------------------------------------------------------------ */
  /* cart                                                                */
  /* ------------------------------------------------------------------ */

  var openCartWithVariantId = function (variantId) {
    if (!drawer) return;
    drawer.dispatchEvent(new CustomEvent('theme:drawer:open', {
      detail: { variant: variantId, reinit: true },
      bubbles: true
    }));
  };

  var addProductVariantToCart = async function (variantId) {
    try {
      var response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      var data = await response.json();
      if (data.status === 422) {
        console.error('Error occurred while adding product to cart:', data.description);
        return;
      }
      openCartWithVariantId(variantId);
      body.classList.remove('modal-view-product-open');
    } catch (error) {
      console.error('Error occurred while adding product to cart:', error);
    }
  };

  /* ------------------------------------------------------------------ */
  /* markup builders — original class names, so the old CSS applies      */
  /* ------------------------------------------------------------------ */

  var escapeHtml = function (str) {
    return String(str === null || str === undefined ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  };

  var makeImage = function (src, alt, fallback, dataAttribute) {
    return '<img src="' + escapeHtml(src || fallback) + '" alt="' + escapeHtml(alt || defaultAltText) + '" loading="lazy" ' +
      (dataAttribute || '') + ' onerror="this.onerror=null;this.src=\'' + escapeHtml(fallback) + '\';">';
  };

  // The _300x size suffix only makes sense on Shopify CDN .png paths (that is
  // all the old data ever had); anything else renders at its natural URL.
  var sizedImageUrl = function (imageUrl) {
    if (!imageUrl || imageUrl.indexOf('.png') === -1) return imageUrl;
    var split = imageUrl.split('.png');
    return split[0] + '_300x.png' + (split[1] || '');
  };

  var makeProductInstruction = async function (instruction, currentStepImage) {
    var image = await fetchImageURL(currentStepImage && currentStepImage.value);
    return '<div class="swiper-slide"><div class="product-card__instruction">' +
      '<div class="product-card__instruction-image">' + makeImage(image && image.url, instruction.value, defaultImage) + '</div>' +
      '<p>' + escapeHtml(instruction.value) + '</p>' +
      '</div></div>';
  };

  var makeProductInstructions = async function (instructionSet) {
    if (!instructionSet) return '';
    var stepName = /^step_.+_name$/i;
    var stepImage = /^step_.+_image$/i;
    var slides = (await Promise.all(instructionSet.map(async function (instruction) {
      if (!stepName.test(String(instruction.key).toLowerCase())) return '';
      if (!instruction.value) return '';
      var stepNumber = instruction.key.split('_')[1];
      var currentStepImage = instructionSet.find(function (f) {
        return stepImage.test(String(f.key).toLowerCase()) && f.key.indexOf(stepNumber) !== -1;
      });
      return makeProductInstruction(instruction, currentStepImage);
    }))).join('');
    if (!slides) return '';
    return '<div data-swiper-container class="swiper-container" style="overflow: hidden">' +
      '<div class="swiper-pagination" style="position: relative;"></div>' +
      '<div class="swiper-wrapper">' + slides + '</div>' +
      '</div>';
  };

  var makeProductCard = async function (step, index, badge) {
    var hasVariant = !!step.dtcVariantId;
    var imgUrl = sizedImageUrl(step.image) || defaultProductImage;
    var dataAttribute = hasVariant ? 'data-view-product' : '';
    var instructionSet = await fetchInstructionSet(step.slot);
    var productInstructions = await makeProductInstructions(instructionSet);
    var category = categoryForSlot(step.slot);
    var meta = (category || badge)
      ? '<div class="product-card__meta">' +
        (category ? '<span class="product-card__cat">' + escapeHtml(category) + '</span>' : '') +
        (badge ? '<span class="product-card__ampm">' + escapeHtml(badge) + '</span>' : '') +
        '</div>'
      : '';
    return '<div data-show data-key="' + step.key + '" class="product-card">' +
      '<span class="product-card__stepnum">' + (index + 1) + '</span>' +
      '<div ' + (dataAttribute ? dataAttribute + ' style="cursor: pointer"' : '') + ' class="product-card__image">' +
      makeImage(imgUrl, step.title, defaultProductImage, dataAttribute) +
      (dataAttribute ? '<div class="product-card__view"><button class="button-secondary" data-view-product>View</button></div>' : '') +
      '</div>' +
      '<div class="product-card__content">' +
      meta +
      '<h3 class="product-card__title">' + escapeHtml(step.title) + '</h3>' +
      (dataAttribute ? '<span class="product-card__hint desktop-hidden">Tap product to learn more</span>' : '') +
      '<div class="product-card__instructions">' + productInstructions + '</div>' +
      '</div></div>';
  };

  var makeProductsList = async function (steps, badge) {
    if (!steps.length) return '<h3 class="no-products__title">' + escapeHtml(noProductsError) + '</h3>';
    var cards = (await Promise.all(steps.map(function (step, index) { return makeProductCard(step, index, badge); }))).join('');
    return '<button data-product-cards="prev" aria-label="Previous step" class="product-cards__button left desktop-hidden">&#8249;</button>' +
      '<div class="product-cards">' + cards + '</div>' +
      '<button data-product-cards="next" aria-label="Next step" class="product-cards__button right desktop-hidden">&#8250;</button>';
  };

  var makeHeading = function (label, badge, cssClass) {
    return '<div class="products-container__heading ' + cssClass + '">' +
      '<h2 class="products-container__heading-title">' + label + ' Routine</h2>' +
      '<span class="products-container__heading-badge">' + badge + '</span></div>';
  };

  var makeProgressBar = function (count) {
    if (!count) return '';
    var stepNumbers = Array.from({ length: count }, function (_, i) { return i + 1; });
    var defaultWidth = (100 / count).toFixed(2);
    return '<div class="progress desktop-hidden">' +
      '<div class="progress-bar"><div style="width: ' + defaultWidth + '%" class="progress-bar__line"></div></div>' +
      '<div class="progress-bar__steps">' + stepNumbers.map(function (n) { return '<div class="progress-bar__step">Step ' + n + '</div>'; }).join('') + '</div>' +
      '</div>';
  };

  var makeProductsContainerAndHeading = async function (steps, label, badge, cssClass) {
    var emptyClass = steps.length === 0 ? 'no-products' : '';
    var productsList = await makeProductsList(steps, badge);
    return '<div class="product-time__container" data-current-slide="0">' +
      makeHeading(label, badge, cssClass) + makeProgressBar(steps.length) +
      '<div class="products-container ' + emptyClass + '">' + productsList + '</div>' +
      '</div>';
  };

  var changeRoutineSection = function (currentType) {
    var routineText = currentType === 'full' ? 'Complete' : 'Starter';
    var typeLine = skinType
      ? '<p class="change-routine__type-line" style="text-align:center;font-size:14px;margin:4px 0 0">Built for your Baumann Skin Type&reg; <b>' + escapeHtml(skinType) + '</b> &middot; <a href="' + RESULTS_URL + '">View &amp; shop your full results &rarr;</a></p>'
      : '<p class="change-routine__type-line" style="text-align:center;font-size:14px;margin:4px 0 0"><a href="' + RESULTS_URL + '">View &amp; shop your full results &rarr;</a></p>';
    var toggle = sizesDiffer
      ? '<div class="add-routine__type"><span>Complete Routine</span>' +
        '<div data-routine-type-change class="add-routine__cta"><span data-routine-type-change></span></div>' +
        '<span>Starter Routine</span></div>'
      : '';
    return '<div class="change-routine">' +
      '<h5 class="change-routine__title">How To Apply Your ' + routineText + ' Routine</h5>' +
      typeLine + toggle +
      '</div>';
  };

  var makeViewProductModal = function (step) {
    var node = productDetailByVariant[step.dtcVariantId];
    var product = node && node.product;
    var title = (product && product.title) || step.title || '';
    var priceAmount = node && node.price ? Number(node.price.amount) : (step.priceMsrp !== null && step.priceMsrp !== undefined ? Number(step.priceMsrp) : null);
    var description = (product && product.description) || '';
    var imgSrc = sizedImageUrl((product && product.featuredImage && product.featuredImage.url) || step.image) || defaultProductImage;
    var ingredientsArr = product && product.ingredients && product.ingredients.value
      ? product.ingredients.value.split(',') : null;
    var skinTypesArr = null;
    try {
      skinTypesArr = product && product.skinTypes && product.skinTypes.value ? JSON.parse(product.skinTypes.value) : null;
    } catch (e) { skinTypesArr = null; }
    var available = node ? node.availableForSale !== false : step.inStock !== false;
    var keyIngredientsImgUrl = savedValues.keyIngredientsImgUrl || '';

    return '<div class="modal-view__close"><button data-view-product="close" class="modal-view__close-button">x</button></div>' +
      '<div class="modal-view__content">' +
      '<div class="modal-view__content-product">' +
      '<div class="modal-view__content-product-image">' + makeImage(imgSrc, title, defaultImage) + '</div>' +
      '<div class="modal-view__content-product-info">' +
      '<h5 class="modal-view__content-product-title">' + escapeHtml(title) + '</h5>' +
      (priceAmount !== null && isFinite(priceAmount) ? '<p class="modal-view__content-product-price">$' + priceAmount.toFixed(2) + '</p>' : '') +
      '<div class="modal-view_button">' +
      (available
        ? '<button data-variant-id="' + escapeHtml(step.dtcVariantId) + '" class="button-primary">Add to cart</button>'
        : '<button class="button-primary" disabled>Out of stock</button>') +
      '</div></div></div>' +
      '<div class="modal-view__content-content">' +
      (description ? '<div class="modal-view__content-desc"><p>' + escapeHtml(description) + '</p></div>' : '') +
      (ingredientsArr ? '<div class="modal-view__ingredients"><h5 class="modal-view__ingredients-title">Full ingredient list</h5>' +
        '<div class="modal-view__ingredients-container">' + ingredientsArr.map(function (i) { return '<span>' + escapeHtml(i) + '</span>'; }).join(', ') + '</div></div>' : '') +
      (skinTypesArr && skinTypesArr.length ? '<div class="modal-view__recomended"><h5 class="modal-view__recomended-title">Recommended for Skin Types</h5>' +
        '<div class="modal-view__recomended-container">' + skinTypesArr.map(function (t) {
          return '<div class="modal-view__recomended-item">' +
            '<span class="modal-view__recomended-item-icon">' + makeImage(keyIngredientsImgUrl + t + '.png', t, defaultImage) + '</span>' +
            '<span class="modal-view__recomended-item-text">' + escapeHtml(t) + '</span></div>';
        }).join('') + '</div></div>' : '') +
      '</div>';
  };

  /* ------------------------------------------------------------------ */
  /* render + interactions                                               */
  /* ------------------------------------------------------------------ */

  var initSwipers = function () {
    if (typeof Swiper === 'undefined') return;
    document.querySelectorAll('[data-swiper-container]').forEach(function (el) {
      new Swiper(el, {
        slidesPerView: 1,
        spaceBetween: 20,
        pagination: { el: el.querySelector('.swiper-pagination'), clickable: true }
      });
    });
  };

  var currentSteps = function () {
    return regimensPage.dataset.productsShow === 'less' ? starterSteps : allSteps;
  };

  var renderRoutineSections = async function () {
    var steps = currentSteps();
    var sections = await Promise.all([
      makeProductsContainerAndHeading(filterByTime(steps, 'AM'), 'Morning', 'AM', 'morning'),
      makeProductsContainerAndHeading(filterByTime(steps, 'PM'), 'Evening', 'PM', 'evening')
    ]);
    return sections.join('');
  };

  toggleLoader(true);
  await loadProductDetails();
  var initialSections = await renderRoutineSections();
  toggleLoader(false);

  regimensPage.innerHTML =
    changeRoutineSection(regimensPage.dataset.productsShow) +
    '<div class="product-container">' + initialSections + '</div>' +
    '<div class="modal-view-product">' +
    '<div data-view-product="close" class="modal-view__overlay"></div>' +
    '<div data-modal-view-product-container class="modal-view-product-container"></div>' +
    '</div>';
  initSwipers();

  regimensPage.addEventListener('click', function (e) {
    var target = e.target;

    if (target.matches('[data-view-product]')) {
      if (target.dataset.viewProduct === 'close') {
        body.classList.remove('modal-view-product-open');
        return;
      }
      var productCard = target.closest('.product-card[data-show]');
      if (!productCard) return;
      var step = byKey[productCard.getAttribute('data-key')];
      if (!step || !step.dtcVariantId) return;
      var productViewModal = document.querySelector('[data-modal-view-product-container]');
      productViewModal.innerHTML = makeViewProductModal(step);
      body.classList.toggle('modal-view-product-open');

    } else if (target.matches('[data-routine-type-change]')) {
      var currentType = regimensPage.dataset.productsShow;
      currentType = showTypes.filter(function (t) { return t !== currentType; })[0];
      regimensPage.setAttribute('data-products-show', currentType);
      var heading = target.closest('.change-routine').querySelector('.change-routine__title');
      heading.textContent = 'How To Apply Your ' + (currentType === 'full' ? 'Complete' : 'Starter') + ' Routine';
      var productsContainers = regimensPage.querySelector('.product-container');
      renderRoutineSections().then(function (html) {
        productsContainers.innerHTML = html;
        initSwipers();
      });

    } else if (target.matches('[data-product-cards]')) {
      var dir = target.dataset.productCards;
      var currentParent = target.closest('.product-time__container');
      var currentSlide = +currentParent.getAttribute('data-current-slide');
      var currentProgress = currentParent.querySelector('.progress-bar__line');
      var currentContainer = currentParent.querySelector('.product-cards');
      var cardsCount = currentParent.querySelectorAll('.product-card').length;
      if (dir === 'prev') {
        if (currentSlide === 0) return;
        currentSlide--;
      } else {
        if (currentSlide === cardsCount - 1) return;
        currentSlide++;
      }
      currentContainer.style.transform = 'translateX(-' + (currentSlide * 100) + '%)';
      currentParent.setAttribute('data-current-slide', currentSlide);
      if (currentProgress) currentProgress.style.width = ((currentSlide + 1) * 100 / cardsCount) + '%';

    } else if (target.matches('[data-variant-id]')) {
      addProductVariantToCart(target.getAttribute('data-variant-id'));
    }
  });
})();
